import { AdEnginePlugin, type Hooks } from "../../core/AdEnginePlugin";
import type {
  BaseSlotName,
  SlotConfig,
  SlotConfigMap,
} from "../../config/platforms/types";
import type { AdNodeManager } from "../../core/AdNodeManager";
import { isElementBelowTheFirstViewport } from "../../helpers/isElementBelowTheFirstViewport";
import { getApp } from "../../helpers/getApp";
import { getContentSelector } from "../../helpers/getContentSelector";

export const MAX_INCONTENT_AD_WIDTH = 970;

/**
 * RepeatableSlotsPlugin is responsible for rendering repeatable slots on the page.
 * It reads the configuration from the slot config and creates multiple slots based on the selector.
 * The slots are inserted into the anchor elements specified in the configuration.
 * The number of slots is limited by the `max` property in the configuration.
 * If the `max` property is not set, it will create as many slots as there are anchors.
 * If the `max` property is set to 0, it will not create any slots.
 * Slots will only be placed if it fits the content width and there is at least one viewport height between anchors,
 *   ensuring only one slot will be displayed on the viewport at one time. Any anchors within one viewport height
 *   of each other will be skipped.
 *
 * This is useful for repeatable slots like in-content ads. For single dynamic slots like floor_adhesion, use DynamicSlotPlugin.
 */
export class RepeatableSlotsPlugin extends AdEnginePlugin {
  private readonly contentWidth: number;

  constructor() {
    super();
    this.contentWidth = this.calculateContentWidth();
  }

  private createSlotConfig(slotConfig: SlotConfig, i: number): SlotConfig {
    return {
      ...slotConfig,
      name: `${slotConfig.name as BaseSlotName}_${i}`,
    };
  }

  private calculateContentWidth(): number {
    const content = document.querySelector(getContentSelector(getApp()));

    return content ? content.clientWidth : MAX_INCONTENT_AD_WIDTH;
  }

  /**
   * Evaluates if the provided HTML Element meets the criteria for rendering an ad slot. The checks
   * ensure that the following conditions are met:
   *   - Anchor element should not be within in the first viewport
   *   - Ads should not be placed in the same viewport as another ad
   *
   * @param anchor The HTML Element that matches the repeatable slot selected being evaluated for an ad
   * @param previousAnchor The previous HTML anchor element that had an ad placed on it to evaluate
   * if the current anchor is too close
   * @returns True if the anchor meets criteria for an ad, false if the anchor should not have an ad
   */
  private shouldPlaceSlotOnAnchor(
    anchor: HTMLElement,
    previousAnchor: HTMLElement | null,
  ) {
    // Element must be below the first viewport
    if (!isElementBelowTheFirstViewport(anchor)) {
      return false;
    }

    // Anchor should not be shorter than the content
    if (anchor.offsetWidth < this.contentWidth) {
      return false;
    }

    const viewportHeight = window.innerHeight;
    // Is this anchor in the same viewport as the previous?
    if (previousAnchor !== null) {
      const minOffsetTopForNextAd = previousAnchor.offsetTop + viewportHeight;
      if (anchor.offsetTop < minOffsetTopForNextAd) {
        return false;
      }
    }

    return true;
  }

  hooks: Hooks = {
    onAfterAdNodeManagerInit: (
      adNodeManager: AdNodeManager,
      slotConfigMap: SlotConfigMap["baseSlots"],
    ) => {
      Object.values(slotConfigMap).forEach((slotConfig) => {
        if (slotConfig?.dynamic?.repeat) {
          const repeatConfig = slotConfig.dynamic.repeat;
          const anchors = [
            ...document.querySelectorAll<HTMLElement>(repeatConfig.selector),
          ];

          let prevAnchor: HTMLElement | null = null;
          const anchorsToUse = anchors.filter((anchor) => {
            if (this.shouldPlaceSlotOnAnchor(anchor, prevAnchor)) {
              prevAnchor = anchor;
              return true;
            }
          });

          const max = Math.min(
            repeatConfig.max ?? Number.MAX_SAFE_INTEGER,
            anchorsToUse.length,
          );

          for (let i = 0; i < max; i++) {
            // Create a new slot config for each repeatable slot, start indexing from 1
            const newSlotConfig = this.createSlotConfig(slotConfig, i + 1);

            const anchor = anchorsToUse[i];
            const placeholder = document.createElement("div");
            const slotElement = document.createElement("div");

            placeholder.classList.add(slotConfig.name);
            placeholder.appendChild(slotElement);

            slotElement.id = newSlotConfig.name;
            anchor[repeatConfig.insertMethod](placeholder);

            adNodeManager.createAdNode(newSlotConfig.name, newSlotConfig);
          }
        }
      });
    },
  };
}
